/* ════════════════════════════════════════════════════════════════════════
   HOME OS SECTION THEMES — single source of truth for the section
   identity inside the Home Operating System.

   HOME OS is the overarching framework. Each section is treated like an
   "app" running inside the OS, with its own palette, gradient, icon, and
   intended feeling. This file feeds:
     • Sidebar accents
     • Sub-navigation
     • Section page headers
     • Dashboard module tiles
     • Bottom mobile nav

   Keep this list short, intentional, and section-based. Do NOT introduce
   random module colours — use only the values defined here.
   ════════════════════════════════════════════════════════════════════════ */

import {
  Sparkles,        // Today's Overview / command centre
  WalletCards,     // Financials
  CalendarDays,    // Schedules
  Utensils,        // Meals
  Bath,            // Cleaning
  Wrench,          // Maintenance
  HeartPulse,      // Health
  PartyPopper,     // Holidays & Events
  FolderLock,      // Documents
  PackageCheck,    // Inventory (was Supplies)
  HeartHandshake,  // Grateful Tree (was Mood)
  MonitorPlay,     // Wall Display (kept for backward-compat; merged into Today)
  Images,          // Memories & Media
  ClipboardList,   // Family Plan
  ContactRound,    // Contacts
} from "lucide-react";

/**
 * Section schema:
 *   id          – stable identifier (used by config/router)
 *   label       – display name
 *   description – one-line purpose (for tooltips / a11y / subnav)
 *   icon        – lucide-react component
 *   color       – primary hex for the section (used for badges, dots)
 *   light       – soft tinted bg (chips, hover, surfaces)
 *   dark        – deeper hex for active states / text on light
 *   gradient    – decorative gradient (hero strips, icon bubbles)
 *   feeling     – short emotional descriptor (UX guard-rail)
 *   sample      – sample card treatment hint (informational only)
 */
export const HOME_OS_SECTION_THEMES = {
  today: {
    id: "today",
    label: "Today's Overview",
    description: "Household command centre, daily rhythm, ARA nudges.",
    icon: Sparkles,
    color: "#0D9488",
    light: "#CCFBF1",
    dark: "#0F766E",
    gradient: "linear-gradient(135deg, #0D9488 0%, #38BDF8 60%, #93C5FD 100%)",
    feeling: "Fresh, intelligent, active, clear",
    sample: "White card, teal accent rule, soft sky bento tiles.",
  },
  familyplan: {
    id: "familyplan",
    label: "Family Plan",
    description: "Goals, budgets and the household plan for this cycle.",
    icon: ClipboardList,
    color: "#0F3D2E",
    light: "#DCFCE7",
    dark: "#0F3D2E",
    gradient: "linear-gradient(135deg, #0F3D2E 0%, #166534 50%, #14B8A6 100%)",
    feeling: "Trusted, organised, intentional",
    sample: "Deep kawakawa header, calm tabular plan rows.",
  },
  financial: {
    id: "financial",
    label: "Financials",
    description: "Money, budget, cashflow, goals, subscriptions.",
    icon: WalletCards,
    color: "#166534",
    light: "#DCFCE7",
    dark: "#0F3D2E",
    gradient: "linear-gradient(135deg, #0F3D2E 0%, #16A34A 60%, #14B8A6 100%)",
    feeling: "Trust, control, calm confidence",
    sample: "Deep kawakawa header, mint chips, tabular numbers.",
  },
  schedules: {
    id: "schedules",
    label: "Schedules",
    description: "Family calendar, routines, school, work, flow.",
    icon: CalendarDays,
    color: "#4F46E5",
    light: "#E0E7FF",
    dark: "#3730A3",
    gradient: "linear-gradient(135deg, #38BDF8 0%, #6366F1 60%, #8B5CF6 100%)",
    feeling: "Time, rhythm, flow, clarity",
    sample: "Sky → indigo timeline, soft violet event chips.",
  },
  meals: {
    id: "meals",
    label: "Meals",
    description: "Meal planning, recipes, shopping list, nutrition.",
    icon: Utensils,
    color: "#D97706",
    light: "#FEF3C7",
    dark: "#B45309",
    gradient: "linear-gradient(135deg, #F59E0B 0%, #FB923C 50%, #84CC16 100%)",
    feeling: "Warm, appetising, nourishing",
    sample: "Golden header, herb-green accents, Pinterest cards.",
  },
  cleaning: {
    id: "cleaning",
    label: "Cleaning",
    description: "Room resets, chores, weekly cleaning rhythm.",
    icon: Bath,
    color: "#0891B2",
    light: "#CFFAFE",
    dark: "#155E75",
    gradient: "linear-gradient(135deg, #06B6D4 0%, #3B82F6 60%, #E0F2FE 100%)",
    feeling: "Fresh, reset, organised, practical",
    sample: "Cyan bento tiles, slate text, white reset cards.",
  },
  maintenance: {
    id: "maintenance",
    label: "Maintenance",
    description: "House repairs, jobs, suppliers and upkeep.",
    icon: Wrench,
    color: "#0369A1",
    light: "#E0F2FE",
    dark: "#0C4A6E",
    gradient: "linear-gradient(135deg, #0369A1 0%, #0EA5E9 60%, #94A3B8 100%)",
    feeling: "Solid, prepared, capable",
    sample: "Deep sky header, slate tool chips, ordered job list.",
  },
  health: {
    id: "health",
    label: "Health",
    description: "Appointments, meds, symptoms, care, wellness.",
    icon: HeartPulse,
    color: "#E11D48",
    light: "#FFE4E6",
    dark: "#9F1239",
    gradient: "linear-gradient(135deg, #F43F5E 0%, #FB7185 60%, #C4B5FD 100%)",
    feeling: "Care, softness, safety, support",
    sample: "Rose chips, lavender supportive notes, soft borders.",
  },
  holidays: {
    id: "holidays",
    label: "Holidays & Events",
    description: "Birthdays, Christmas, parties, gifts, travel.",
    icon: PartyPopper,
    color: "#F97316",
    light: "#FFEDD5",
    dark: "#C2410C",
    gradient: "linear-gradient(135deg, #FB7185 0%, #FBBF24 50%, #F59E0B 100%)",
    feeling: "Joy, celebration, family memories",
    sample: "Coral → peach gradient banners, gold accents.",
  },
  memories: {
    id: "memories",
    label: "Memories & Media",
    description: "Photos, videos, albums and family memories.",
    icon: Images,
    color: "#DB2777",
    light: "#FCE7F3",
    dark: "#831843",
    gradient: "linear-gradient(135deg, #DB2777 0%, #F97316 45%, #FACC15 100%)",
    feeling: "Nostalgic, warm, joyful, visual, family legacy",
    sample: "Raspberry hero, large photo grid, gold album covers.",
  },
  documents: {
    id: "documents",
    label: "Documents",
    description: "Forms, bills, renewals, insurance, records.",
    icon: FolderLock,
    color: "#6D28D9",
    light: "#EDE9FE",
    dark: "#4C1D95",
    gradient: "linear-gradient(135deg, #4C1D95 0%, #7C3AED 60%, #64748B 100%)",
    feeling: "Secure, organised, calm admin vault",
    sample: "Royal violet header, slate folder chips, cool grey rows.",
  },
  supplies: {
    id: "supplies",
    label: "Inventory",
    description: "Pantry, household stock, low-stock and shopping.",
    icon: PackageCheck,
    color: "#65A30D",
    light: "#ECFCCB",
    dark: "#3F6212",
    gradient: "linear-gradient(135deg, #65A30D 0%, #84CC16 50%, #A7F3D0 100%)",
    feeling: "Stocked, practical, fresh, ready",
    sample: "Fresh green dots, lime chips, mint surfaces.",
  },
  contacts: {
    id: "contacts",
    label: "Contacts",
    description: "Family members, household contacts and trusted people.",
    icon: ContactRound,
    color: "#0F766E",
    light: "#CCFBF1",
    dark: "#134E4A",
    gradient: "linear-gradient(135deg, #0F766E 0%, #14B8A6 60%, #93C5FD 100%)",
    feeling: "Connected, trusted, accessible",
    sample: "Muted teal header, avatar chips, calm address list.",
  },
  mood: {
    id: "mood",
    label: "Grateful Tree",
    description: "Daily check-ins, gratitude notes, family connection.",
    icon: HeartHandshake,
    color: "#8B5CF6",
    light: "#EDE9FE",
    dark: "#6D28D9",
    gradient: "linear-gradient(135deg, #C4B5FD 0%, #A78BFA 50%, #F0ABFC 100%)",
    feeling: "Gentle, emotionally safe, supportive",
    sample: "Lavender bento, soft violet wheel, rainbow accent.",
  },
  wall: {
    id: "wall",
    label: "Wall Display",
    description: "Family command centre on the kitchen tablet.",
    icon: MonitorPlay,
    color: "#0F172A",
    light: "#E2E8F0",
    dark: "#020617",
    gradient: "linear-gradient(180deg, #0F172A 0%, #1E3A8A 60%, #38BDF8 100%)",
    feeling: "Smart home, calm command centre, premium display",
    sample: "Navy backdrop, teal glow, oversized typography.",
  },
};

/**
 * Canonical order the OS renders sections in (sidebar + bento + subnav).
 * Today is always first (the OS home screen).
 */
export const HOME_OS_SECTION_ORDER = [
  "today",
  "familyplan",
  "schedules",
  "financial",
  "meals",
  "cleaning",
  "maintenance",
  "health",
  "documents",
  "supplies",
  "contacts",
  "holidays",
  "memories",
  "mood",
];

export const HOME_OS_SECTION_LIST = HOME_OS_SECTION_ORDER.map(
  (id) => HOME_OS_SECTION_THEMES[id]
);

/** Lookup helper that always returns a valid theme (falls back to Today). */
export function sectionTheme(id) {
  return HOME_OS_SECTION_THEMES[id] || HOME_OS_SECTION_THEMES.today;
}

/**
 * Map legacy module ids → new section ids. Lets existing pages/components
 * (which still use the older `HOME_OS_MODULES` keys) participate in the new
 * section design system without route changes.
 */
export const LEGACY_MODULE_TO_SECTION = {
  today:      "today",
  familyplan: "familyplan",
  money:      "financial",
  calendar:   "schedules",
  meals:      "meals",
  cleaning:   "cleaning",
  chores:     "cleaning",
  maintenance:"maintenance",
  health:     "health",
  documents:  "documents",
  supplies:   "supplies",
  contacts:   "contacts",
  mood:       "mood",
  family:     "mood",
  memories:   "memories",
  ai:         "today",
  report:     "today",
  setup:      "today",
  wall:       "today",   // wall display is now a mode of Today's Overview
};

export function sectionForModule(moduleId) {
  return sectionTheme(LEGACY_MODULE_TO_SECTION[moduleId] || "today");
}
